/**
 * ThisWeekPreviewWidget — dashboard card showing the next 7 days of scheduled posts.
 *
 * Fetches `GET /api/dashboard/this-week` (returns 7-day skeleton with grouped posts).
 * Each day card shows: weekday + date, post count, and a vertical stack of compact
 * post chips (platform · client · title · time). Clicking a chip opens that post in
 * My Posts (?id=…). Empty days show a soft "—" so the rhythm is visible at a glance.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CalendarRange, Loader2, ChevronRight } from 'lucide-react';

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
};

const STATUS_DOT = {
  approved: 'bg-emerald-500',
  changes_requested: 'bg-orange-500',
  rejected: 'bg-rose-500',
  pending_review: 'bg-amber-400',
};

export const ThisWeekPreviewWidget = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/dashboard/this-week');
        if (!cancelled) setData(r.data);
      } catch (_) {
        if (!cancelled) setData({ days: [], total: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  return (
    <Card data-testid="this-week-preview-widget">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarRange className="w-4 h-4 text-indigo-600" />
              This week's preview
            </CardTitle>
            <CardDescription className="text-xs">
              {loading
                ? 'Loading…'
                : `${data?.total ?? 0} scheduled post${data?.total === 1 ? '' : 's'} in the next 7 days`}
            </CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={() => navigate('/calendar')} data-testid="open-calendar-btn">
            Open calendar <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
        ) : (data?.days || []).every((d) => d.count === 0) ? (
          <div className="text-center py-8 text-sm text-zinc-500 space-y-2">
            <p>No posts scheduled this week.</p>
            <Button size="sm" variant="outline" onClick={() => navigate('/weekly-themes')} data-testid="open-themes-btn">
              Use Weekly Themes to fill the week
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
            {data.days.map((day) => (
              <div
                key={day.date}
                className={`rounded-lg border p-2 min-h-[120px] flex flex-col ${
                  day.is_today
                    ? 'border-indigo-300 bg-indigo-50/40'
                    : day.count === 0
                      ? 'border-zinc-100 bg-zinc-50/50'
                      : 'border-zinc-200'
                }`}
                data-testid={`day-${day.date}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <div>
                    <p className={`text-xs font-medium ${day.is_today ? 'text-indigo-700' : 'text-zinc-600'}`}>
                      {day.label_short}
                    </p>
                    <p className="text-[10px] text-zinc-400">{day.label_full.split(' ').slice(1).join(' ')}</p>
                  </div>
                  {day.count > 0 && (
                    <Badge variant="secondary" className="text-[10px] h-4 px-1.5">{day.count}</Badge>
                  )}
                </div>
                <div className="flex-1 space-y-1 overflow-hidden">
                  {day.posts.length === 0 ? (
                    <p className="text-[11px] text-zinc-300 mt-3">—</p>
                  ) : (
                    day.posts.slice(0, 4).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => navigate(`/posts?id=${p.id}`)}
                        className="w-full text-left p-1.5 rounded bg-white border border-zinc-100 hover:border-indigo-300 transition-colors"
                        data-testid={`post-chip-${p.id}`}
                        style={p.client_brand_color ? { borderLeft: `3px solid ${p.client_brand_color}` } : undefined}
                      >
                        <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                          <span className="font-medium text-zinc-700">{formatTime(p.scheduled_for)}</span>
                          <span>·</span>
                          <span className="truncate">{p.platform}</span>
                          {p.review_status && STATUS_DOT[p.review_status] && (
                            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[p.review_status]}`} title={p.review_status} />
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-800 truncate font-medium">
                          {p.post_title || p.caption_preview || '(no title)'}
                        </p>
                        {p.client_name && (
                          <p className="text-[10px] text-zinc-400 truncate">{p.client_name}</p>
                        )}
                      </button>
                    ))
                  )}
                  {day.posts.length > 4 && (
                    <p className="text-[10px] text-zinc-400 pl-1">+{day.posts.length - 4} more</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ThisWeekPreviewWidget;
